import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { MultiSelect } from "primereact/multiselect";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import { getCoupons, saveCoupon, deleteCoupon, STORAGE_PREFIX } from "../../services/brandingService";
import { ActivePill, ImagePicker, CARD_BG, CARD_BORDER, DIVIDER, fmtDate, ghostBtn, inputStyle, labelStyle, primaryBtn } from "./shared";

// A coupon: name, discount percent, start / end date, active - plus, optionally, the
// profiles and services it is limited to (saved in the coupon's child table). Leaving
// both lists empty means it applies to every profile and service of the vendor.
const EMPTY = { id: null, name: "", discount_percent: "", partner_ids: [], service_ids: [], start_date: "", end_date: "", is_active: true, image: null };

// The dropdown panel opens on <body>; lift it above the modal.
const panelProps = { appendTo: typeof document !== "undefined" ? document.body : undefined, autoZIndex: false, panelStyle: { zIndex: 2000 } };

export default function CouponsTab({ catalog }) {
  const [coupons, setCoupons] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCoupons();
      setCoupons(res?.data || []);
    } catch (e) {
      toast.error(e?.message || "Could not load coupons");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The vendor's profiles, same list the booking page uses.
  useEffect(() => {
    apicall.get("partners").then((res) => setPartners(res?.data || [])).catch(() => {});
  }, []);

  const openForm = (c) => setForm(c ? { ...EMPTY, ...c, original: snapshot(c), saved_image: c.image || null, start_date: c.start_date || "", end_date: c.end_date || "", is_active: !!c.is_active } : { ...EMPTY });
  // Saved image comes back as a URL; a newly picked one is a data URL.
  // What the coupon looked like when the form opened - used to spot an Update with nothing changed.
  const snapshot = (c) => JSON.stringify({
    name: String(c.name || "").trim(),
    percent: Number(c.discount_percent),
    partners: [...(c.partner_ids || [])].map(Number).sort((a, b) => a - b),
    services: [...(c.service_ids || [])].map(Number).sort((a, b) => a - b),
    start: c.start_date || "",
    end: c.end_date || "",
    active: !!c.is_active,
    image: c.image || null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const name = form.name.trim();
    if (name.length < 2) return toast.error("Enter the coupon name");
    const percent = Number(form.discount_percent);
    if (!form.discount_percent) return toast.error("Enter the discount percent");
    if (!(percent > 0) || percent > 100) return toast.error("Discount percent must be above 0 and at most 100");
    if (!form.partner_ids.length) return toast.error("Select at least one profile");
    if (!form.start_date) return toast.error("Select the start date");
    if (!form.end_date) return toast.error("Select the end date");
    if (form.start_date > form.end_date) return toast.error("The end date cannot be before the start date");
    if (form.id && form.original === snapshot(form)) return toast.info("No changes detected");
    setSaving(true);
    try {
      const res = await saveCoupon({
        id: form.id || undefined,
        name,
        discount_percent: percent,
        partner_ids: form.partner_ids,
        service_ids: form.service_ids,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        is_active: form.is_active,
        storage_prefix: STORAGE_PREFIX,
        ...(form.image?.startsWith("data:") ? { image: form.image } : {}),
        ...(!form.image && form.saved_image ? { remove_image: true } : {}),
      });
      if (res?.success) {
        toast.success("Coupon saved");
        setForm(null);
        load();
      } else {
        toast.error(res?.message || "Could not save the coupon");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the coupon ${c.name}?`)) return;
    const res = await deleteCoupon(c.id);
    if (res?.success) {
      toast.success("Coupon deleted");
      load();
    } else {
      toast.error(res?.message || "Could not delete the coupon");
    }
  };

  const namesOf = (ids, list, labelKey, all) => {
    if (!ids?.length) return all;
    const names = ids.map((id) => list.find((x) => x.id === id)?.[labelKey]).filter(Boolean);
    return names.length ? names.join(", ") : `${ids.length} selected`;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>Percentage discounts your team can apply while creating a booking.</div>
        <button type="button" style={primaryBtn} onClick={() => openForm(null)}>+ Add coupon</button>
      </div>

      <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 14, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading coupons…</div>
        ) : coupons.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No coupons yet. Click “Add coupon” to create one.</div>
        ) : (
          coupons.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderBottom: `1px solid ${DIVIDER}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {c.image && <img src={c.image} alt="" width={28} height={28} style={{ objectFit: "cover", borderRadius: 6 }} />}
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>{c.name}</span>
                  <ActivePill active={!!c.is_active} />
                </div>
                <div style={{ fontSize: 12.5, color: "#334155", marginTop: 2 }}>
                  {Number(c.discount_percent)}% off · {namesOf(c.service_ids, catalog, "name", "All services")} · {namesOf(c.partner_ids, partners, "partner_name", "All profiles")}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                  {fmtDate(c.start_date)} – {fmtDate(c.end_date)}
                </div>
              </div>
              <button type="button" style={{ ...ghostBtn, padding: "5px 14px", fontSize: 12 }} onClick={() => openForm(c)}>Edit</button>
              <button type="button" style={{ ...ghostBtn, padding: "5px 14px", fontSize: 12, color: "#b91c1c", borderColor: "#fecaca" }} onClick={() => remove(c)}>Delete</button>
            </div>
          ))
        )}
      </div>

      <Modal show={!!form} onHide={() => !saving && setForm(null)} centered scrollable>
        {form && (
          <>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 17 }}>{form.id ? "Edit coupon" : "New coupon"}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={labelStyle}>Coupon name <span style={{ color: "#d01c4c" }}>*</span></label>
                <input style={inputStyle} value={form.name} maxLength={40} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Welcome offer" />
              </div>
              <div>
                <ImagePicker value={form.image} onChange={(img) => set("image", img)} label="Upload an Image:" />
              </div>
              <div>
                <label style={labelStyle}>Discount (%) <span style={{ color: "#d01c4c" }}>*</span></label>
                <input type="number" min="0" max="100" style={inputStyle} value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Profiles <span style={{ color: "#d01c4c" }}>*</span></label>
                <MultiSelect
                  value={form.partner_ids}
                  onChange={(e) => set("partner_ids", e.value || [])}
                  options={partners}
                  optionLabel="partner_name"
                  optionValue="id"
                  filter
                  showClear
                  placeholder="Select profiles"
                  maxSelectedLabels={2}
                  style={{ width: "100%", height: "38px" }}
                  {...panelProps}
                />
              </div>
              <div>
                <label style={labelStyle}>Services</label>
                <MultiSelect
                  value={form.service_ids}
                  onChange={(e) => set("service_ids", e.value || [])}
                  options={catalog}
                  optionLabel="name"
                  optionValue="id"
                  filter
                  showClear
                  placeholder="All services"
                  maxSelectedLabels={2}
                  style={{ width: "100%", height: "38px" }}
                  {...panelProps}
                />
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>Leave services empty to offer this coupon on all services of the selected profiles.</div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Starts <span style={{ color: "#d01c4c" }}>*</span></label>
                  <input type="date" style={inputStyle} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Ends <span style={{ color: "#d01c4c" }}>*</span></label>
                  <input type="date" style={inputStyle} min={form.start_date || undefined} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#334155", cursor: "pointer" }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
                Coupon is active
              </label>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" style={ghostBtn} disabled={saving} onClick={() => setForm(null)}>Cancel</button>
              <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={submit}>
                {saving ? (form.id ? "Updating…" : "Saving…") : form.id ? "Update" : "Save"}
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
}
